import { gql } from '@apollo/client';

export const BOOKING_FIELDS_FRAGMENT = gql`
  fragment BookingFields on Booking {
    _id
    boutiqueId
    createdAt
    modifiedAt
    createdBy
    modifiedBy
  }
`;

export const BOOKING_TEMPLATE_FIELDS_FRAGMENT = gql`
  fragment BookingTemplateFields on BookingTemplate {
    _id
    booking {
      _id
    }
    type
    fields {
      fieldId
      translations {
        label
        language
        value
      }
      radioValues {
        itemId
        label
        language
      }
      selectValues {
        itemId
        label
        language
      }
      type
      system
      required
    }
  }
`;

export const GET_BOOKINGS_LISTING = gql`
  query bookings($orderBy: [BookingOrderByExp!] = { _id: ASC }, $where: BookingBoolExp) {
    bookings(order_by: $orderBy, where: $where) {
      _id
      createdAt
      modifiedAt
      createdBy
      modifiedBy
    }
  }
`;

export const GET_BOOKING_BY_BOUTIQUE = gql`
  ${BOOKING_FIELDS_FRAGMENT}
  query booking($boutiqueId: String!) {
    booking: bookingByBoutique(boutiqueId: $boutiqueId) {
      ...BookingFields
    }
  }
`;

export const BOOKING_STAFF_FRAGMENT = gql`
  fragment BookingStaffFields on BookingStaff {
    _id
    bookable
    color
    externalUserId
    role
    notify
    useBusinessHours
    workingHours {
      weekday
      timeslots {
        hourFrom
        hourTo
      }
    }
  }
`;

export const GET_BOOKING_STAFF_MEMBERS = gql`
  ${BOOKING_STAFF_FRAGMENT}
  query bookingStaffs($orderBy: [BookingStaffOrderByExp!] = { _id: ASC }, $where: BookingStaffBoolExp) {
    bookingStaffs(order_by: $orderBy, where: $where) {
      ...BookingStaffFields
    }
  }
`;

export const BOOKING_CATEGORY_FRAGMENT = gql`
  fragment BookingCategoryFields on BookingCategory {
    _id
    boutiqueId
    internalId
    displayName
    description
    isHiddenFromCustomers
    defaultDurationMinutes
    iconName
    staffMembers {
      _id
    }
    languages
    minHoursRequiredPriorToCancelling
    minHoursRequiredPriorToRescheduling
    staffMembersMode
    createdAt
    createdBy
  }
`;

export const GET_BOOKING_CATEGORIES = gql`
  query bookingCategories($orderBy: [BookingCategoryOrderByExp!] = { _id: ASC }, $where: BookingCategoryBoolExp) {
    bookingCategories(order_by: $orderBy, where: $where) {
      _id
      boutiqueId
      internalId
      displayName
      defaultDurationMinutes
      iconName
      languages
    }
  }
`;

export const GET_BOOKING_CATEGORY = gql`
  ${BOOKING_CATEGORY_FRAGMENT}
  query bookingCategory($_id: String!) {
    bookingCategory(_id: $_id) {
      ...BookingCategoryFields
    }
  }
`;

export const CREATE_OR_UPDATE_BOOKING_CATEGORY = gql`
  ${BOOKING_CATEGORY_FRAGMENT}
  mutation upsertOneBookingCategory($query: BookingCategoryQueryInput!, $data: BookingCategoryInsertInput!) {
    result: upsertOneBookingCategory(query: $query, data: $data) {
      value {
        ...BookingCategoryFields
      }
    }
  }
`;

export const GET_BOOKING_TEMPLATES = gql`
  ${BOOKING_TEMPLATE_FIELDS_FRAGMENT}
  query bookingTemplates($orderBy: [BookingTemplateOrderByExp!] = { _id: ASC }, $where: BookingTemplateBoolExp) {
    bookingTemplates(order_by: $orderBy, where: $where) {
      ...BookingTemplateFields
    }
  }
`;

export const GET_BOOKING_TEMPLATE_BY_BOOKING_AND_TYPE = gql`
  ${BOOKING_TEMPLATE_FIELDS_FRAGMENT}
  query bookingTemplateByBookingAndType($bookingId: String!, $type: String!) {
    bookingTemplate: bookingTemplateByBookingAndType(bookingId: $bookingId, type: $type) {
      ...BookingTemplateFields
    }
  }
`;

export const GET_BOOKING_SETTINGS_BY_BOOKING = gql`
  query bookingSetting($bookingId: String!) {
    bookingSetting: bookingSettingByBooking(bookingId: $bookingId) {
      _id
      boutiqueId
      openingTimeRange {
        hourFrom
        hourTo
      }
      businessHours {
        timeslots {
          hourFrom
          hourTo
        }
        weekday
      }
      customBusinessHours {
        timeslots {
          hourFrom
          hourTo
        }
        date
        source
      }
      maximumAdvance
      minimumLeadTime
      timeslotsDefaultDuration
      timeslotsDefaultGap
      timeslotsMaxAdditionalGuests
      timeslotsMaxCapacity
      timeslotsMaxSimultaneous
      minHoursRequiredPriorToCancelling
      languages
      enableQrCode
      enableWallet
      enableAdditionalGuests
      enableEmailNotification
      enableCapacityRestriction
      enableClientAppointmentInSharedCalendar
      enableDynamicTimeslots
      enableFixedTimeslots
      enableMarketingReachability
      disallowGuestChangeBooking
      disallowGuestCancelBooking
    }
  }
`;

export const GET_BOOKING_TIMESLOT = gql`
  query bookingTimeslot($_id: String!) {
    bookingTimeslot(_id: $_id) {
      _id
      date
      hourFrom
      hourTo
      maxGuests
      invites {
        _id
        boutiqueId
        blockAllCommunications
        isNoShow
        additionalGuests {
          _id
          firstName
          lastName
        }
        bookingCustomFields {
          field
          value
        }
        confirmationEmailResendAt
        guest {
          _id
          crmId
          firstName
          lastName
          email
          phone
          title
          language
          invites {
            _id
            blockAllCommunications
            additionalGuests {
              _id
            }
          }
        }
        createdBy
        createdAt
        modifiedBy
        modifiedAt
        session {
          _id
        }
        timeslot {
          _id
          date
          hourFrom
          hourTo
          fromVirtual
          maxGuests
          staffAssignment {
            inviteId
            staffId
          }
          category {
            _id
            internalId
          }
          masterclassData {
            id
            displayName
          }
        }
      }
      guestsCount
      additionalGuestsCount
      booking {
        _id
      }
      category {
        _id
        internalId
        displayName
        iconName
        languages
      }
      staffAssignment {
        inviteId
        staffId
      }
      fromVirtual
      language
      masterclassData {
        displayName
      }
      assignedStaff {
        _id
        displayName
      }
      isPrivate
    }
  }
`;

export const GET_BOOKING_TIMESLOTS_FOR_CALENDAR = gql`
  query bookingTimeslotsForCalendar($orderBy: [BookingTimeslotOrderByExp!], $where: BookingTimeslotBoolExp) {
    bookingTimeslots(order_by: $orderBy, where: $where) {
      _id
      date
      hourFrom
      hourTo
      maxGuests
      guestsCount
      additionalGuestsCount
      category {
        _id
        internalId
        displayName
        iconName
      }
      invites {
        _id
        blockAllCommunications
        isNoShow
        guest {
          _id
        }
        createdBy
        timeslot {
          _id
        }
      }
      staffAssignment {
        inviteId
        staffId
      }
      fromVirtual
      language
      isPrivate
      masterclassData {
        displayName
      }
      assignedStaff {
        _id
        displayName
      }
    }
  }
`;

export const UPDATE_BOOKING_SETTINGS = gql`
  mutation replaceOneBookingSetting($query: BookingSettingQueryInput!, $data: BookingSettingInsertInput!) {
    result: replaceOneBookingSetting(query: $query, data: $data) {
      value {
        _id
      }
    }
  }
`;

export const UPDATE_BOOKING_STAFF = gql`
  ${BOOKING_STAFF_FRAGMENT}
  mutation replaceOneBookingStaff($query: BookingStaffQueryInput!, $data: BookingStaffInsertInput!) {
    result: replaceOneBookingStaff(query: $query, data: $data) {
      value {
        ...BookingStaffFields
      }
    }
  }
`;

export const UPDATE_BOOKING_TEMPLATE = gql`
  ${BOOKING_TEMPLATE_FIELDS_FRAGMENT}
  mutation updateOneBookingTemplate($query: BookingTemplateQueryInput!, $set: BookingTemplateUpdateInput!) {
    result: updateOneBookingTemplate(query: $query, set: $set) {
      value {
        ...BookingTemplateFields
      }
    }
  }
`;

export const DELETE_BOOKING_CATEGORIES = gql`
  mutation deleteManyBookingCategories($ids: [String!]!) {
    deleteManyBookingCategories(ids: $ids) {
      deletedCount
    }
  }
`;

export const CREATE_BOOKING_TIMESLOTS = gql`
  mutation insertManyBookingTimeslots($data: [BookingTimeslotInsertInput!]!) {
    result: insertManyBookingTimeslots(data: $data) {
      insertedIds
    }
  }
`;

export const UPDATE_BOOKING_TIMESLOT = gql`
  mutation updateOneBookingTimeslot($query: BookingTimeslotQueryInput!, $set: BookingTimeslotUpdateInput!) {
    result: updateOneBookingTimeslot(query: $query, set: $set) {
      value {
        _id
      }
    }
  }
`;

export const DELETE_BOOKING_TIMESLOTS = gql`
  mutation deleteManyBookingTimeslots($ids: [String!]!) {
    deleteManyBookingTimeslots(ids: $ids) {
      deletedCount
    }
  }
`;

export const GET_GUEST_INVITE_BY_QR_CODE = gql`
  query guestInviteByQrCodeContent($qrCodeContent: String!) {
    guestInvite: guestInviteByQrCodeContent(qrCodeContent: $qrCodeContent) {
      _id
      boutiqueId
      guest {
        _id
        firstName
        lastName
        segment
        subSegment
      }
      additionalGuests {
        _id
        firstName
        lastName
      }
      timeslot {
        _id
        date
        hourFrom
        hourTo
        category {
          _id
          displayName
          iconName
        }
        masterclassData {
          displayName
        }
      }
    }
  }
`;
